export type EntitlementSource =
  | 'plan'
  | 'addon'
  | 'trial'
  | 'plan_included'
  | 'paid'
  | 'off';

export type BlockedReason =
  | 'on_hold'
  | 'cancelled'
  | 'trial_ended'
  | 'period_lapsed'
  | 'no_subscription';

/**
 * Which billing system governs the account (`user_accounts.billing_engine`).
 * `frozen` is the mid-cutover state: neither engine may act on it yet.
 */
export type BillingEngine = 'legacy' | 'newbilling' | 'frozen';

export interface EntitlementCapMeter {
  included: number | null;
  addon: number | null;
  total: number | null;
  used: number | null;
}

export interface EntitlementModuleState {
  active: boolean;
  source: EntitlementSource;
}

/** The payload for a caller WITH `access_billing`. */
export interface FullEntitlements {
  limited?: false;
  /**
   * Absent on an api that predates the field; the FE then keeps treating a
   * 200 as NewBilling-governed.
   */
  billingEngine?: BillingEngine;
  plan: {
    slug: string | null;
    tier: string | null;
    label: string | null;
  };
  status: {
    isTrial: boolean;
    isActive: boolean;
    isPastDue: boolean;
    isExpired: boolean;
    isLastPaymentFailed: boolean;
    blockedReason: BlockedReason | null;
  };
  features: Record<string, { label: string; source: EntitlementSource }>;
  modules: Record<string, EntitlementModuleState>;
  caps: {
    seats: EntitlementCapMeter;
    profiles: EntitlementCapMeter;
  };
  credits: {
    ai: {
      allowance: number | null;
      used: number | null;
      balance: number | null;
      unlimited: boolean;
    };
  };
}

/**
 * The payload for a caller WITHOUT `access_billing` (artist, client,
 * translator…; api#1079). Enough to gate access and features, nothing
 * commercial: no plan, caps, credits, `isActive`, `isPastDue` or
 * `isLastPaymentFailed`. A missing cap is UNKNOWN, never "0 allowed" — the api
 * enforces seat and profile limits server-side.
 */
export interface LimitedEntitlements {
  limited: true;
  billingEngine?: BillingEngine;
  status: {
    isExpired: boolean;
    isTrial: boolean;
    /** null unless expired */
    blockedReason: BlockedReason | null;
  };
  features: Record<string, { active: boolean }>;
  modules: Record<string, { active: boolean }>;
}

export interface EntitlementsResponse {
  /** Accepted at the top level too, wherever the api settles on putting it. */
  billingEngine?: BillingEngine;
  entitlements: FullEntitlements | LimitedEntitlements;
}

export const isLimitedEntitlements = (
  entitlements: FullEntitlements | LimitedEntitlements | undefined,
): entitlements is LimitedEntitlements => entitlements?.limited === true;

/**
 * The full payload, or undefined while loading and for the limited view. For
 * the billing page and cap / credit readers, which only make sense with it.
 */
export const fullEntitlementsOf = (
  response: EntitlementsResponse | undefined,
): FullEntitlements | undefined => {
  const entitlements = response?.entitlements;
  return entitlements && !isLimitedEntitlements(entitlements)
    ? entitlements
    : undefined;
};

/**
 * The `billingEngine` of an entitlements payload, or undefined when the api did
 * not send one (or no payload has loaded yet).
 */
export const resolveBillingEngine = (
  response: EntitlementsResponse | undefined,
): BillingEngine | undefined =>
  response?.entitlements?.billingEngine ?? response?.billingEngine;

/**
 * Whether the account is governed by NewBilling, from its engine. Only
 * `newbilling` is; `legacy` and `frozen` (mid-cutover) are not. An api without
 * the field (undefined) keeps the older routes-based answer (true here).
 */
export const isNewBillingGovernedEngine = (
  engine: BillingEngine | undefined,
): boolean => engine === undefined || engine === 'newbilling';

/**
 * Whether the v2 billing page should send the account to legacy billing.
 * Deliberately only `legacy`, not `frozen`: a frozen account is mid-cutover and
 * legacy billing would let it buy a Braintree plan (product call, see PR).
 */
export const shouldBounceToLegacyBilling = (
  engine: BillingEngine | undefined,
): boolean => engine === 'legacy';
